<script lang="ts">
  import {project} from '@/store';
  import {Button} from '@components';
  import {dndzone, SHADOW_ITEM_MARKER_PROPERTY_NAME} from 'svelte-dnd-action';
  import {cubicIn} from 'svelte/easing';
  import {removePunctuation, sortIndexes} from '@/utils/common';
  import {createEventDispatcher, onMount} from 'svelte';
  import type {Sentence, Word} from '@/types/project';
  import {toasts} from 'svelte-toasts';
  import {flip} from 'svelte/animate';
  import {fade} from 'svelte/transition';
  const dispatch = createEventDispatcher();

  export let dropZone = false;
  export let sentence: Sentence;
  export let isLastSentence: boolean;
  export let isFirstSentence: boolean;
  export let searchWord: string = '';
  export let dropFromOthersDisabled: boolean = true;
  export let isAdvancedSearch: boolean;
  let wordBeingEditted: Word;
  let action: string;
  let dragDisabled = true;
  let dropTargetStyle = {};
  let block;
  const flipDurationMs = 300;

  const getAccuracy = (level: number) => {
    return level >= 90 ? 'bg-slate-100' : 'bg-yellow-400';
  };

  const prepareSentence = (items: any) => {
    dragDisabled = true;
    items = sortIndexes(items);
    sentence.words = [...items];
  };

  const handleConsider = (e: CustomEvent) => {
    e.preventDefault();
    prepareSentence(e.detail.items);
  };

  const handleFinalize = (index: number, e: CustomEvent) => {
    e.preventDefault();
    prepareSentence(e.detail.items);
    dispatch('on-finalize', {sentence});
  };

  const dispatchDrag = (action: string) => {
    dispatch('disable-others', {
      action,
      index: sentence.index
    });
  };

  const startDrag = (index: number) => {
    dragDisabled = false;
    const wordIndex = sentence.words.findIndex(item => item.index === index);
    const isFirstIndex = wordIndex === 0;
    const isLastIndex = wordIndex === sentence.words.length - 1;

    if (sentence.words.length === 1) {
      dragDisabled = true;
      return false;
    }

    if ((isFirstSentence && isFirstIndex) || (!isFirstIndex && !isLastIndex) || (isLastSentence && isLastIndex)) {
      dragDisabled = true;
      return false;
    }

    if (isFirstIndex && isLastIndex) {
      dispatchDrag('BOTH');
      return;
    }

    action = isFirstIndex ? 'UP' : isLastIndex ? 'DOWN' : '';
    dispatchDrag(action);
  };

  const transformDraggedElement = (element: HTMLElement | undefined) => {
    if (!element) return;
    element.classList.add('outline-2', 'outline', 'outline-neutral');
  };

  let disableNewLine = (e: any) => {
    if (e.which === 13) {
      e.preventDefault();
      e.target.blur();
    }
  };

  const onFocus = (word: Word, event: FocusEvent) => {
    event.preventDefault();
    wordBeingEditted = word;
    event.target?.addEventListener('keypress', disableNewLine);
    dragDisabled = true;
  };

  const onBlur = (word: Word, event: FocusEvent) => {
    event.preventDefault();
    dispatchDrag('');
    dragDisabled = true;
    const target = event.target as HTMLDivElement;
    target.removeEventListener('keypress', disableNewLine);
  };

  const onInput = (word: Word, event: Event) => {
    event.preventDefault();
    const target = event.target as HTMLDivElement;
    if (!window.saveTooltip) {
      toasts.add({
        title: 'Quick Tip',
        description: 'You can use CTRL+S to save your changes.',
        duration: 3000,
        placement: 'bottom-right',
        type: 'info',
        theme: 'light'
      });

      window.saveTooltip = true;
    }

    if (!target) return;

    if (target.textContent != word?.word) {
      word.word = target.textContent as string;
      project.insertChange({
        word: word
      });
    }
  };

  const stopDrag = () => {
    dragDisabled = true;
    dispatchDrag('');
  };

  // const transformDraggedElement = (element: HTMLElement | undefined) => {
  //   if (!element) return;
  //   element.classList.add("outline-2", "outline", "outline-neutral");
  // };

  const selectSentence = (e: any) => {
    sentence.selected = true;
    dispatch('select-sentence', {
      sentence,
      shiftKey: e.shiftKey
    });
  };

  const unselectSentence = (e: any) => {
    sentence.selected = false;
    dispatch('unselect-sentence', {
      sentence,
      shiftKey: e.shiftKey
    });
    project.unselectSentence(sentence);
  };

  const playSentence = () => {
    dispatch('play-sentence', {
      index: sentence.index
    });
  };
</script>

<div
  id="block-{sentence.index}"
  bind:this={block}
  data-sentence-id={sentence.index}
  class={`sentence-block mb-6 mr-6 rounded-xl bg-white shadow-md ${sentence.selected ? 'border-4 border-[#f0456f] ' : 'border border-gray-300'}`}
>
  <div class="sub-item mt-2 flex flex-col rounded-t-md">
    <section
      on:consider={handleConsider}
      on:finalize={e => handleFinalize(sentence.index, e)}
      class="flex flex-grow flex-wrap gap-2 p-4 {dropZone ? 'ss-dnd-border' : ''}"
      use:dndzone={{
        dragDisabled,
        dropFromOthersDisabled,
        flipDurationMs,
        dropTargetStyle,
        items: sentence.words,
        transformDraggedElement
      }}
    >
      {#each sentence.words as item, i (item.index)}
        <div class="word-box" animate:flip={{duration: flipDurationMs}}>
          <div
            on:input={e => onInput(item, e)}
            on:focus={e => onFocus(item, e)}
            on:blur={e => onBlur(item, e)}
            class={`flex !select-text flex-row break-all rounded border-1.6 border-solid ${getAccuracy(item.accuracy)} ${dropZone ? 'bg-white' : 'bg-opacity-20'} ${searchWord.toLowerCase() === removePunctuation(item.word).toLowerCase() ? 'border-neutral' : 'border-slate-200'} px-1`}
          >
            {#if i === 0 && !isFirstSentence}
              <div
                class="mr-1 h-4 w-3 cursor-grab"
                on:touchstart={() => startDrag(item.index)}
                on:mousedown={() => startDrag(item.index)}
                on:touchend={() => stopDrag()}
                on:mouseup={() => stopDrag()}
              >
                <i class="fa-sharp fa-regular fa-grip-dots-vertical cursor-grab text-gray-500"></i>
              </div>
            {/if}
            <p contenteditable={true}>{item.word}</p>
            {#if item[SHADOW_ITEM_MARKER_PROPERTY_NAME]}
              <div in:fade={{duration: 200, easing: cubicIn}} class="custom-shadow-item">
                {item.word}
              </div>
            {/if}
            {#if !isLastSentence && i === sentence.words.length - 1}
              <div
                class="ml-1 h-4 w-3 cursor-grab"
                on:touchstart={() => startDrag(item.index)}
                on:mousedown={() => startDrag(item.index)}
                on:touchend={() => stopDrag()}
                on:mouseup={() => stopDrag()}
              >
                <i class="fa-sharp fa-regular fa-grip-dots-vertical cursor-grab text-gray-500"></i>
              </div>
            {/if}
          </div>
        </div>
      {/each}
    </section>
    <div class="flex justify-between gap-2 rounded-b-md border-t border-t-gray-200 px-4 py-2">
      <div class="flex items-center justify-start">
        <!-- ADD BUTTON -->
        <Button
          icon="Plus"
          iconSize="small"
          variant="primary"
          on:click={selectSentence}
          className="!w-6 !h-6 add_sentence rounded-lg mr-2 {sentence.selected ? 'hidden' : ''}"
        />
        <!-- MINUS BUTTON -->
        <Button
          icon="Minus"
          iconSize="small"
          variant="subtract"
          on:click={unselectSentence}
          className="!w-6 !h-6 rounded-lg mr-2 {sentence.selected ? '' : 'hidden'}"
        />
        <!-- PLAY BUTTON -->
        <Button
          iconSize="small"
          variant="primaryOutline"
          className="h-6 w-6 !border rounded-lg"
          on:click={playSentence}
        >
          <i class="fa-solid fa-circle-play"></i>
        </Button>
      </div>
      <div class="flex">
        {#if isAdvancedSearch && sentence.selected}
          <span class="rounded-lg bg-primary px-2 py-1 text-sm font-bold text-white"
            ><i class="fa-solid fa-sparkles"></i> AI</span
          >
        {/if}
      </div>
    </div>
  </div>
</div>

<style lang="postcss">
  .handle-wrapper {
    @apply absolute right-0 mr-1 h-4 w-4 cursor-grab;
  }

  .handle {
    @apply relative h-full w-full;
  }

  .handle:before {
    @apply absolute left-0 text-slate-200;
    content: '.';
    top: -4px;
    font-size: 16px;
    line-height: 5px;
    text-shadow:
      0 5px rgb(226 232 240),
      0 10px rgb(226 232 240),
      5px 0 rgb(226 232 240),
      5px 5px rgb(226 232 240),
      5px 10px rgb(226 232 240),
      10px 0 rgb(226 232 240),
      10px 5px rgb(226 232 240),
      10px 10px rgb(226 232 240);
  }

  .word-box {
    @apply relative flex items-center;
  }

  .custom-shadow-item {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    visibility: visible;
    border: 1px dashed grey;
    background: lightblue;
    opacity: 0.5;
    margin: 0;
  }

  .ss-dnd-border {
    border: 2px dashed #b5b5b5;
    border-radius: 5px;
  }
</style>
